'use client';

import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import type { CMSHeroSlide } from '@/lib/cms/types';
import { EVENT_NAME } from '@/lib/gtm/const';
import { resolveViewPromotionFromCms } from '@/lib/gtm/promotion';
import { useCallback, useEffect, useRef } from 'react';

interface UseHeroSliderPromotionTrackingOptions {
  slides: CMSHeroSlide[];
  activeIndex: number;
}

/**
 * Tracks `view_promotion` for HeroSlider slides:
 * - fires when the slider enters the viewport
 * - fires again when the active slide changes (one event per slide, no re-scroll duplicates)
 */
export function useHeroSliderPromotionTracking({
  slides,
  activeIndex,
}: UseHeroSliderPromotionTrackingOptions): (node: HTMLElement | null) => void {
  const viewedSlidesRef = useRef<Set<number>>(new Set());
  const inViewRef = useRef(false);
  const slidesRef = useRef(slides);
  const activeIndexRef = useRef(activeIndex);
  const observerRef = useRef<IntersectionObserver | null>(null);
  slidesRef.current = slides;
  activeIndexRef.current = activeIndex;

  const { sendPromotionEvent } = useGoogleAnalytics();
  const sendPromotionEventRef = useRef(sendPromotionEvent);
  sendPromotionEventRef.current = sendPromotionEvent;

  const trackSlide = useCallback((index: number) => {
    const slide = slidesRef.current[index];
    if (!slide || viewedSlidesRef.current.has(index)) {
      return;
    }

    const promotionParams = resolveViewPromotionFromCms(slide, index);
    if (!promotionParams) {
      return;
    }

    viewedSlidesRef.current.add(index);
    sendPromotionEventRef
      .current(EVENT_NAME.VIEW_PROMOTION, promotionParams)
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    return () => observerRef.current?.disconnect();
  }, []);

  const setContainerRef = useCallback(
    (node: HTMLElement | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;

      if (!node || slidesRef.current.length === 0) {
        return;
      }

      observerRef.current = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting || inViewRef.current) {
            return;
          }

          inViewRef.current = true;
          trackSlide(activeIndexRef.current);
        },
        { threshold: 0.1 }
      );

      observerRef.current.observe(node);
    },
    [trackSlide]
  );

  useEffect(() => {
    if (!inViewRef.current) {
      return;
    }

    trackSlide(activeIndex);
  }, [activeIndex, trackSlide]);

  return setContainerRef;
}
